import DigiobcanApi from '../../api/DigiobcanApi';
import { createQueryFn, QueryNameSpace, useQ } from '../../lib/query';
import type { QueryFunctionContext } from '@tanstack/react-query';

type Params = {
  view?: 'flat' | 'tree';
  /**
   * Hľadaný výraz. Filtruje **API**, nie prehliadač: hľadá v ceste kategórie,
   * v jej identifikátore a - volajúcemu s `pruser:report-categories:manage` -
   * aj v menách zodpovedných úradníkov, ktoré klient inak vôbec nedostane.
   *
   * Pri neprázdnom `q` je odpoveď vždy plochá, aj keď sa pýtaš `view: 'tree'`:
   * vo výsledku nemusí byť rodič nájdenej kategórie, takže by sa jej zaradenie
   * stratilo. Nesie ho `full_path`.
   */
  q?: string;
};

export const getReportCategories = createQueryFn(
  QueryNameSpace.getReportCategories,
  async ({ queryKey }: QueryFunctionContext<[string] | [string, Params]>) => {
    const [, params] = queryKey;

    const { report_categories } =
      await DigiobcanApi.getReportCategories(params);

    return report_categories;
  },
);

/**
 * Identifikátor predvolenej kategórie - tej, do ktorej padne podnet, ktorý sa
 * nepodarilo zaradiť.
 *
 * Zdroj pravdy je príznak `is_default` v databáze, nie konfigurácia. Kým bol
 * v `config.DEFAULT_REPORT_CATEGORY_ID`, musel sa zhodovať s `unassignedCategoryId`
 * v `reports-processor` a s tým, čo naozaj bolo v tabuľke - tri miesta, ktoré
 * sa nemali ako držať v zhode, a hodnota sa navyše dala zmeniť len nasadením.
 *
 * Vracia `undefined`, kým sa zoznam nenačíta, **aj** keď predvolená kategória
 * žiadna nie je (dá sa zmazať). Volajúci sa preto nesmie spoliehať na to, že
 * nejaká existuje - obrazovka sa má správať ako predtým, nie spadnúť.
 *
 * Zoznam kategórií je krátky, načítava ho aj niekoľko ďalších obrazoviek
 * a mení sa zriedka, takže je to dotaz zdieľaný z cache, nie ďalší request.
 */
export const useDefaultReportCategoryId = (): string | undefined => {
  const { data } = useQ(getReportCategories, undefined, {
    staleTime: 5 * 60 * 1000,
  });

  return data?.find((category) => category.is_default)?.category_id;
};
